import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Send, LoaderCircle } from "lucide-react";
import { ScrollReveal } from "./Reveal";
import { trackLead, trackCompleteRegistration, newEventId } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { nume: "", email: "", telefon: "", nivel: "", categorie_varsta: "", sursa: "", mesaj: "" };

const COPY = {
  proba: {
    eyebrow: "Lecție gratuită de probă",
    title: "Primul pas.",
    text: "Completezi formularul, iar noi îți trimitem ulterior pe email ora și locația lecției de probă. Vii, cunoști atmosfera, apoi decizi.",
    note: "Ora și locația lecției de probă îți sunt comunicate ulterior, pe email.",
  },
  inscriere: {
    eyebrow: "Înscriere directă",
    title: "Sunt decis(ă).",
    text: "N-ai nevoie de lecția de probă? Perfect. Completezi formularul, iar noi îți trimitem pe email detaliile de plată (transfer bancar) și confirmarea locului tău.",
    note: "Detaliile de plată și confirmarea locului îți sunt comunicate pe email.",
  },
};

const inputCls =
  "w-full bg-transparent border-b border-sumi/25 focus:border-hanko outline-none py-3.5 text-base text-sumi placeholder:text-charcoal/40 transition-colors duration-300 rounded-none";

export default function TrialForm() {
  const [tip, setTip] = useState("proba");
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const copy = COPY[tip];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nivel || !form.categorie_varsta || !form.sursa) {
      toast.error("Alege nivelul, categoria de vârstă și cum ai aflat de noi.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/trial-leads`, { ...form, tip });
      if (tip === "proba") {
        trackLead({ content_name: "Free trial class", value: 500, currency: "RON", eventID: newEventId() });
        navigate("/thank-you-trial");
      } else {
        trackLead({ content_name: "Course sign-up", value: 1450, currency: "RON", eventID: newEventId() });
        trackCompleteRegistration({ content_name: "Course sign-up", value: 1450, currency: "RON", eventID: newEventId() });
        navigate("/thank-you-course");
      }
    } catch (err) {
      toast.error("Ceva n-a mers. Încearcă din nou sau scrie-ne pe email.");
      setLoading(false);
    }
  };

  return (
    <section id="lectie" data-testid="trial-section" className="relative py-20 sm:py-32 overflow-hidden">
      <span aria-hidden className="pointer-events-none select-none absolute -left-10 bottom-0 font-jp text-[14rem] leading-none text-sumi/[0.04] hidden lg:block">
        体験
      </span>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <ScrollReveal>
          <AnimatePresence mode="wait">
            <motion.div
              key={tip}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="text-xs font-mono tracking-[0.3em] uppercase text-hanko mb-4">{copy.eyebrow}</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-sumi leading-tight">
                <span className="italic text-hanko">{copy.title}</span>
              </h2>
              <p className="mt-6 text-charcoal/80 leading-relaxed max-w-md" data-testid="enroll-intro-text">
                {copy.text}
              </p>
            </motion.div>
          </AnimatePresence>
          <div className="mt-10 relative max-w-sm hidden lg:block">
            <img
              src="https://images.unsplash.com/photo-1657463120947-f445d5e64fb5?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Felinar tradițional japonez"
              loading="lazy"
              className="w-full aspect-[16/10] object-cover"
              data-testid="trial-side-image"
            />
            <div className="absolute inset-0 ring-1 ring-sumi/10" />
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.15}>
          <form
            data-testid="trial-booking-form"
            onSubmit={submit}
            className="bg-white border border-sumi/10 p-8 sm:p-12"
          >
            <div
              data-testid="enroll-type-toggle"
              className="grid grid-cols-2 gap-1.5 p-1.5 bg-washiaccent rounded-full mb-10"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tip === "proba"}
                data-testid="enroll-type-trial"
                onClick={() => setTip("proba")}
                className={`py-3 px-2 rounded-full text-sm font-semibold transition-colors duration-300 min-h-[44px] ${
                  tip === "proba" ? "bg-hanko text-washi" : "text-charcoal/60 hover:text-sumi"
                }`}
              >
                Lecție gratuită
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tip === "inscriere"}
                data-testid="enroll-type-direct"
                onClick={() => setTip("inscriere")}
                className={`py-3 px-2 rounded-full text-sm font-semibold transition-colors duration-300 min-h-[44px] ${
                  tip === "inscriere" ? "bg-hanko text-washi" : "text-charcoal/60 hover:text-sumi"
                }`}
              >
                Mă înscriu direct
              </button>
            </div>

            <div className="space-y-7">
              <div>
                <label htmlFor="trial-nume" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Nume complet *</label>
                <input
                  id="trial-nume"
                  data-testid="trial-form-input-name"
                  required
                  minLength={2}
                  value={form.nume}
                  onChange={set("nume")}
                  placeholder="Numele tău"
                  className={inputCls}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="trial-email" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Email *</label>
                  <input
                    id="trial-email"
                    data-testid="trial-form-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="nume@email.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="trial-telefon" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Telefon *</label>
                  <input
                    id="trial-telefon"
                    data-testid="trial-form-input-phone"
                    type="tel"
                    required
                    minLength={6}
                    value={form.telefon}
                    onChange={set("telefon")}
                    placeholder="07xx xxx xxx"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <div>
                  <label htmlFor="trial-nivel" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Nivel *</label>
                  <select
                    id="trial-nivel"
                    data-testid="trial-form-select-level"
                    required
                    value={form.nivel}
                    onChange={set("nivel")}
                    className={`${inputCls} bg-white cursor-pointer`}
                  >
                    <option value="" disabled>Alege nivelul</option>
                    <option>Începător absolut</option>
                    <option>Am mai studiat puțin</option>
                    <option>Intermediar</option>
                    <option>Avansat</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="trial-varsta" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Vârstă *</label>
                  <select
                    id="trial-varsta"
                    data-testid="trial-form-select-age"
                    required
                    value={form.categorie_varsta}
                    onChange={set("categorie_varsta")}
                    className={`${inputCls} bg-white cursor-pointer`}
                  >
                    <option value="" disabled>Categoria de vârstă</option>
                    <option>Copil (7–12 ani)</option>
                    <option>Adolescent (13–17 ani)</option>
                    <option>Adult (18+)</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="trial-sursa" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Cum ai aflat de noi? *</label>
                <select
                  id="trial-sursa"
                  data-testid="trial-form-select-source"
                  required
                  value={form.sursa}
                  onChange={set("sursa")}
                  className={`${inputCls} bg-white cursor-pointer`}
                >
                  <option value="" disabled>Alege o variantă</option>
                  <option>Instagram</option>
                  <option>Facebook</option>
                  <option>TikTok</option>
                  <option>Recomandare personală</option>
                  <option>Altceva</option>
                </select>
              </div>
              <div>
                <label htmlFor="trial-mesaj" className="text-[11px] font-mono tracking-widest uppercase text-charcoal/60">Mesaj (opțional)</label>
                <textarea
                  id="trial-mesaj"
                  data-testid="trial-form-input-message"
                  rows={3}
                  value={form.mesaj}
                  onChange={set("mesaj")}
                  placeholder="Spune-ne ce te atrage la japoneză…"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>

            <button
              data-testid="trial-form-submit-button"
              type="submit"
              disabled={loading}
              className="mt-10 w-full bg-hanko text-washi font-semibold min-h-[56px] rounded-full hover:bg-hankodeep transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <LoaderCircle size={20} className="animate-spin" /> : <Send size={18} />}
              {loading ? "Se trimite…" : "Trimite înscrierea"}
            </button>
            <p className="mt-5 text-xs text-charcoal/55 leading-relaxed text-center" data-testid="enroll-form-note">
              {copy.note}
            </p>
          </form>
        </ScrollReveal>
      </div>
    </section>
  );
}
